import { useMemo, useState } from "react";
import { Plus, Camera, VideoCamera } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { ExplorerTable } from "./ExplorerTable";
import { fmtDate, fmtTime } from "./serviceApi";
import { RecurringRows } from "./recurring/RecurringRows";

export const StatusBadge = ({ status }) => (
  <span className={`inline-flex items-center px-1.5 py-[1px] rounded text-[10px] font-bold uppercase tracking-wider ${status === "completed" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
    {status === "completed" ? "Completed" : "Draft"}
  </span>
);

export const RecurringBadge = () => (
  <span className="inline-flex items-center px-1.5 py-[1px] rounded text-[10px] font-bold uppercase tracking-wider bg-orange-500 text-white" data-testid="recurring-badge">Recurring</span>
);

const DEFAULT_SORT = { key: "date", dir: "desc" };

export function RecordsPane({ property, records, searchActive, selectedId, onSelect, onNew, loading, schedules = [], onOpenSchedule, overdue, selectedScheduleId }) {
  const [hideRecurring, setHideRecurring] = useState(false);
  const columns = useMemo(() => [
    {
      key: "date", label: "Date", width: 118,
      sortValue: (r) => `${r.service_date} ${r.service_time || ""}`,
      render: (r) => (
        <div className="leading-tight">
          <div className="font-semibold text-slate-800">{fmtDate(r.service_date)}</div>
          {r.service_time && <div className="text-[10px] text-slate-400">{fmtTime(r.service_time)}</div>}
        </div>
      ),
    },
    {
      key: "title", label: "Name", width: 190,
      render: (r) => (
        <div className="leading-tight min-w-0">
          <div className="font-semibold text-slate-800 truncate">{r.title}</div>
          <div className="flex items-center gap-1.5 mt-0.5">
            <StatusBadge status={r.status} />
            {r.recurring_schedule_id && <RecurringBadge />}
            {r.photo_count > 0 && <span className="text-[10px] text-slate-400 inline-flex items-center gap-0.5"><Camera size={10} />{r.photo_count}</span>}
            {r.video_count > 0 && <span className="text-[10px] text-slate-400 inline-flex items-center gap-0.5"><VideoCamera size={10} />{r.video_count}</span>}
          </div>
        </div>
      ),
    },
    { key: "category_name", label: "Category", width: 110 },
    { key: "unit", label: "Unit", width: 90 },
    { key: "service_person_name", label: "Service Person", width: 140 },
  ], []);

  return (
    <div className="h-full flex flex-col bg-white" data-testid="service-pane-records">
      <div className="px-3 pt-3 pb-2 border-b border-slate-200 flex items-center gap-2">
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-widest font-bold text-slate-500">Service history</div>
          <div className="text-sm font-semibold text-slate-800 truncate" data-testid="service-records-property-name">
            {property ? property.street : "Select a property"}
          </div>
        </div>
        <Button size="sm" onClick={onNew} className="h-7 bg-orange-500 hover:bg-orange-600 text-white" data-testid="service-new-entry-btn">
          <Plus size={13} weight="bold" className="mr-1" /> New Service Entry
        </Button>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto">
        {!property ? (
          <div className="p-6 text-center text-sm text-slate-500" data-testid="service-records-no-property">
            Pick an address on the left to see its service history.
          </div>
        ) : loading ? (
          <div className="p-4 text-sm text-slate-400">Loading…</div>
        ) : (
          <>
          <RecurringRows schedules={schedules} hidden={hideRecurring} onToggleHidden={() => setHideRecurring((h) => !h)} onOpen={onOpenSchedule} overdue={overdue} selectedId={selectedScheduleId} />
          <ExplorerTable
            prefKey="service.records.table"
            testId="service-records-table"
            columns={columns}
            rows={records}
            selectedId={selectedId}
            onRowClick={onSelect}
            defaultSort={DEFAULT_SORT}
            emptyText={searchActive ? "No entries at this address match your search." : "No service entries for this address yet."}
          />
          </>
        )}
      </div>
    </div>
  );
}
